/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    IAny,
    ICommerceApiSettings,
    ICreateActionContext,
    IGeneric
} from '@msdyn365-commerce/core';
import { ChannelIdentity } from '@msdyn365-commerce/retail-proxy';
import { getChannelsAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/StoreOperationsDataActions.g';
import { buildCacheKey, QueryResultSettingsProxy } from '@msdyn365-commerce-modules/retail-actions';

/**
 * Interface of Channel Identity..
 */
interface IChannelIdentity {
    channelIdentities: ChannelIdentity[];
}

/**
 * Calls the Retail API and returns the channels.
 * @returns - List of channel identity.
 */
const getChannels = () => async (ctx: IActionContext): Promise<ChannelIdentity[]> => {
    return getChannelsAsync(
        {
            callerContext: ctx,
            queryResultSettings: QueryResultSettingsProxy.getPagingFromInputDataOrDefaultValue(ctx)
        }
    );
};

/**
 * Action input.
 */
export class GetChannelIdentitiesInput implements IActionInput {
    private readonly apiSettings: ICommerceApiSettings;

    constructor(apiSettings: ICommerceApiSettings) {
        this.apiSettings = apiSettings;
    }

    public getCacheKey = () => buildCacheKey('ChannelIdentities', this.apiSettings);

    public getCacheObjectType = () => 'ChannelIdentities';

    public dataCacheType = (): CacheType => 'request';
}

/**
 * Creates the input required to make the retail api call.
 * @param inputData - Action Data Input.
 * @returns - Input object.
 */
const createChannelIdentitiesInput = (inputData: ICreateActionContext<IGeneric<IAny>>) => {
    return new GetChannelIdentitiesInput(inputData.requestContext.apiSettings);
};

/**
 * Get channels action.
 * @param input - Input data.
 * @param context - Action context.
 * @returns - Channel Identity.
 */
export async function getChannelsAction(
    input: GetChannelIdentitiesInput,
    context: IActionContext
): Promise<IChannelIdentity> {
    if (!context) {
        throw new Error('getChannelsAction - Action context cannot be null/undefined');
    }

    let channels: ChannelIdentity[] = [];

    try {
        channels = await getChannels()(context);
    } catch (error) {
        throw error;
    }

    return { channelIdentities: channels };
}

/**
 * Get sales order with hydrations action.
 * @param id - Unique Id for data action.
 * @param context - Action context.
 * @param input - Input for data action.
 * @returns Channel api response.
 */
export const getChannelsActionDataAction = createObservableDataAction({
    id: '@msdyn365-commerce-modules/order-management/get-channels',
    action: <IAction<IChannelIdentity>>getChannelsAction,
    input: createChannelIdentitiesInput
});

export default getChannelsActionDataAction;
